import { useState, useCallback, useRef } from "react";
import { useCustomQuery } from "data/modules/api/hooks/useCustomQuery";
import { ZOOM_ATTENDANCE_CONSTANTS } from "../constants/ZoomAttendance.constants";
import { logError } from "utils/error";

/**
 * Custom hook to fetch meeting details and manage meeting attendance exports.
 *
 * @param {Object} params - Hook parameters.
 * @param {string} params.listingUuid - Unique identifier for the listing.
 * @returns {Object} Meeting details state, pagination controls, search query, refresh, and export functions.
 */
export const useListingMeetingDetails = ({ listingUuid }) => {
  const [page, setPage] = useState(
    ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.INITIAL_PAGE
  );
  const [pageSize, setPageSize] = useState(
    ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.PAGE_SIZE_DEFAULT
  );
  const [searchQuery, setSearchQuery] = useState("");
  const exportInFlightRef = useRef(false);

  const params = {
    listing_uuid: listingUuid,
    page,
    per_page: pageSize,
    search: searchQuery,
  };

  const {
    data: queryData,
    isLoading: loading,
    isFetching,
    refetch: refresh,
  } = useCustomQuery({
    url: ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_MEETING_DETAILS,
    params,
    queryKey: [
      ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_MEETING_DETAILS,
      listingUuid,
      page,
      pageSize,
      searchQuery,
    ],
    staleTime: ZOOM_ATTENDANCE_CONSTANTS.QUERY_CACHE_TIME_MS,
    enabled: Boolean(listingUuid),
    onError: (err) => {
      logError({
        error: err,
        when: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES
          .FETCH_MEETING_DETAILS_ERROR_WHEN,
        occuredAt:
          ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.MEETING_DETAILS_HOOK_OCCURRENCE,
      });
    },
  });

  const rawDetails =
    queryData?.meeting_details || queryData?.data?.meeting_details || [];
  const data = rawDetails.map((item) => ({
    ...item,
    id: item.id || item.slot_uuid,
  }));
  const total =
    queryData?.total ||
    queryData?.data?.total ||
    ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ZERO;
  const totalPages =
    queryData?.total_pages ||
    queryData?.data?.total_pages ||
    ZOOM_ATTENDANCE_CONSTANTS.NUMERIC.ONE;

  const exportAttendance = useCallback(
    async ({ filterValues = {}, notify, slotUuids = [] }) => {
      if (!listingUuid || exportInFlightRef.current) return [];
      exportInFlightRef.current = true;
      const controller = new AbortController();
      const exportParams = {
        listing_uuid: listingUuid,
        page: ZOOM_ATTENDANCE_CONSTANTS.DEFAULT_PAGINATION.PAGE_ALL,
        ...filterValues,
      };

      if (slotUuids && slotUuids.length > 0) {
        exportParams.slot_uuids = slotUuids.join(",");
        exportParams.slot_uuid =
          slotUuids.length === 1 ? slotUuids[0] : slotUuids.join(",");
      }

      try {
        const response = await fetch(
          `${
            ZOOM_ATTENDANCE_CONSTANTS.API_ENDPOINTS.GET_MEETING_DETAILS
          }?${new URLSearchParams(exportParams).toString()}`,
          { signal: controller.signal }
        );
        const resData = await response.json();
        return (
          resData?.data?.meeting_details ||
          resData?.data?.attendance_list ||
          resData?.data ||
          []
        );
      } catch (err) {
        if (err?.name === "AbortError") return [];
        logError({
          error: err,
          when: ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.EXPORT_ATTENDANCE_ERROR_WHEN,
          occuredAt:
            ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.MEETING_DETAILS_HOOK_OCCURRENCE,
        });
        if (notify) {
          notify(ZOOM_ATTENDANCE_CONSTANTS.MESSAGES.EXPORT_FAILURE, "error");
        }
        return [];
      } finally {
        exportInFlightRef.current = false;
      }
    },
    [listingUuid]
  );

  return {
    data,
    total,
    totalPages,
    loading,
    isFetching: Boolean(isFetching),
    page,
    setPage,
    pageSize,
    setPageSize,
    searchQuery,
    setSearchQuery,
    refresh,
    exportAttendance,
  };
};
